/**
 * useSidebarSectionState — persist open/closed state per section in localStorage.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'office-app:sidebar-section:';

function readStorage(key: string): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    return raw === '1';
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function useSidebarSectionState(
  sectionId: string,
  defaultOpen: boolean
): [boolean, () => void, (next: boolean) => void] {
  const storageKey = `${STORAGE_PREFIX}${sectionId}`;
  const [open, setOpenState] = useState<boolean>(() => {
    const stored = readStorage(storageKey);
    return stored ?? defaultOpen;
  });

  useEffect(() => {
    const stored = readStorage(storageKey);
    if (stored !== null && stored !== open) {
      setOpenState(stored);
    }
    // only on mount — further reads are synchronous above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      writeStorage(storageKey, next);
    },
    [storageKey]
  );

  const toggle = useCallback(() => {
    setOpenState((prev) => {
      const next = !prev;
      writeStorage(storageKey, next);
      return next;
    });
  }, [storageKey]);

  return [open, toggle, setOpen];
}
